/**
 * ChatInput — barra inferior del tutor (textarea + botones de pizarra y envío).
 *
 * Memoizada para evitar re-render con cada token de streaming en la lista de
 * mensajes. Solo se re-renderiza si cambia input, loading, topicRequired,
 * showWhiteboard o config (que es estable durante la sesión).
 * Extraída de TutorChat.jsx (v1.3.0).
 */
import React, { memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, PenTool, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import MathWhiteboard from "../MathWhiteboard";
import ScienceWhiteboard from "../ScienceWhiteboard";

function ChatInput({
  input,
  onInputChange,
  onPaste,
  onKeyDown,
  onSend,
  loading,
  topicRequired,
  hasMathSupport,
  scienceMode,
  configColor,
  showWhiteboard,
  setShowWhiteboard,
  onWhiteboardSubmit,
  textareaRef,
}) {
  return (
    <div className="shrink-0 border-t bg-white p-4">
      <div className="max-w-3xl mx-auto">
        {/* Whiteboard collapsible para materias con matemática */}
        {hasMathSupport && (
          <AnimatePresence>
            {showWhiteboard && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 overflow-hidden max-h-[55vh] overflow-y-auto"
              >
                {scienceMode ? (
                  <ScienceWhiteboard onSubmit={onWhiteboardSubmit} subjectColor={configColor} />
                ) : (
                  <MathWhiteboard onSubmit={onWhiteboardSubmit} subjectColor={configColor} />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        <div className="flex gap-3 items-end">
          {hasMathSupport && (
            <Button
              variant={showWhiteboard ? "default" : "outline"}
              size="icon"
              onClick={() => setShowWhiteboard(!showWhiteboard)}
              className={`h-12 w-12 rounded-xl shrink-0 ${
                showWhiteboard ? "text-white" : "text-gray-600 hover:text-gray-900"
              }`}
              style={showWhiteboard ? { backgroundColor: configColor } : {}}
              title={scienceMode ? "Abrir pizarra cientifica" : "Abrir pizarra matematica"}
              data-testid="whiteboard-toggle-btn"
            >
              <PenTool className="w-5 h-5" />
            </Button>
          )}

          <Textarea
            ref={textareaRef}
            value={input}
            onChange={onInputChange}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            placeholder={topicRequired ? "Selecciona un tema para iniciar..." : "Escribe tu respuesta..."}
            className="resize-none min-h-[48px] max-h-[120px] rounded-xl border-gray-200 focus:border-[#419cca] focus:ring-[#419cca]"
            rows={1}
            disabled={loading || topicRequired}
            data-testid="chat-input"
          />
          <Button
            onClick={onSend}
            disabled={!input.trim() || loading || topicRequired}
            className="h-12 w-12 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90"
            data-testid="send-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Send className="w-5 h-5 text-white" />}
          </Button>
        </div>

        {hasMathSupport && !showWhiteboard && (
          <p className="text-xs text-gray-400 mt-2 text-center">
            {scienceMode ? (
              <>
                Usa la{" "}
                <button onClick={() => setShowWhiteboard(true)} className="text-teal-500 hover:underline">
                  pizarra cientifica
                </button>{" "}
                para escribir formulas quimicas, fisicas y ecuaciones
              </>
            ) : (
              <>
                Usa la{" "}
                <button onClick={() => setShowWhiteboard(true)} className="text-purple-500 hover:underline">
                  pizarra matematica
                </button>{" "}
                para escribir ecuaciones y que el tutor las verifique
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default memo(ChatInput);
